import React, { useState, useEffect, useRef } from 'react';
import {
  Building2,
  Phone,
  Mail,
  Save,
  Loader2,
  AlertCircle,
  Check,
  X,
  Settings,
  Info,
  Upload,
  Image,
  Trash2,
} from 'lucide-react';
import {
  type PlatformSettings,
  getPlatformSettings,
  updatePlatformSettings,
  uploadPlatformLogo,
  deletePlatformLogo,
} from '@/services/platformSettingsService';

const API_BASE = `${import.meta.env.VITE_API_BASE_URL || 'http://localhost:4000'}`;

export const PlatformSettingsPage: React.FC = () => {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [uploadingLogo, setUploadingLogo] = useState(false);
  const [deletingLogo, setDeletingLogo] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [currentLogo, setCurrentLogo] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    companyName: '',
    contactPhone: '',
    contactEmail: '',
  });

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      setLoading(true);
      setError(null);
      try {
        const settings = await getPlatformSettings();
        setFormData({
          companyName: settings.companyName,
          contactPhone: settings.contactPhone,
          contactEmail: settings.contactEmail,
        });
        setCurrentLogo(settings.companyLogo);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Erreur lors du chargement');
      } finally {
        setLoading(false);
      }
    };
    loadSettings();
  }, []);

  const handleLogoUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    const allowedTypes = ['image/png', 'image/jpeg', 'image/jpg', 'image/webp', 'image/svg+xml'];
    if (!allowedTypes.includes(file.type)) {
      setError('Format non supporte. Utilisez PNG, JPG, WebP ou SVG.');
      return;
    }

    // Validate file size (2MB max)
    if (file.size > 2 * 1024 * 1024) {
      setError('Le fichier est trop volumineux. Taille maximale: 2 Mo.');
      return;
    }

    setUploadingLogo(true);
    setError(null);
    setSuccess(null);

    try {
      const response = await uploadPlatformLogo(file);
      setCurrentLogo(response.data.companyLogo);
      setSuccess('Logo mis a jour avec succes');
      // Dispatch event for sidebar to update
      window.dispatchEvent(new CustomEvent('platform-logo-updated', {
        detail: { logo: response.data.companyLogo }
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors du telechargement du logo');
    } finally {
      setUploadingLogo(false);
      // Reset file input
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };

  const handleDeleteLogo = async () => {
    if (!currentLogo) return;

    setDeletingLogo(true);
    setError(null);
    setSuccess(null);

    try {
      await deletePlatformLogo();
      setCurrentLogo(null);
      setSuccess('Logo supprime avec succes');
      // Dispatch event for sidebar to update
      window.dispatchEvent(new CustomEvent('platform-logo-updated', {
        detail: { logo: null }
      }));
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la suppression du logo');
    } finally {
      setDeletingLogo(false);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    setSuccess(null);

    try {
      await updatePlatformSettings({
        companyName: formData.companyName,
        contactPhone: formData.contactPhone,
        contactEmail: formData.contactEmail,
      });
      setSuccess('Parametres enregistres avec succes');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Erreur lors de la sauvegarde');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-bold text-gray-900">Informations de Contact</h1>
        <p className="text-gray-500 mt-1">
          Configurez les coordonnees de contact affichees aux etablissements
        </p>
      </div>

      {/* Alerts */}
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 text-red-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-red-800">Erreur</p>
            <p className="text-sm text-red-700">{error}</p>
          </div>
          <button onClick={() => setError(null)} className="ml-auto">
            <X className="w-5 h-5 text-red-600" />
          </button>
        </div>
      )}

      {success && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3">
          <Check className="w-5 h-5 text-green-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-green-800">Succes</p>
            <p className="text-sm text-green-700">{success}</p>
          </div>
          <button onClick={() => setSuccess(null)} className="ml-auto">
            <X className="w-5 h-5 text-green-600" />
          </button>
        </div>
      )}

      {/* Info Card */}
      <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3">
        <Info className="w-5 h-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <p className="text-sm font-medium text-blue-800">Information</p>
          <p className="text-sm text-blue-700">
            Ces informations sont affichees sur la page de suspension lorsqu'un etablissement
            est suspendu pour reglement de facture. Assurez-vous que les coordonnees sont a jour.
          </p>
        </div>
      </div>

      {/* Logo Section */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3">
          <div className="p-2 bg-indigo-100 rounded-lg">
            <Image className="w-5 h-5 text-indigo-600" />
          </div>
          <div>
            <h2 className="font-semibold text-gray-900">Logo de la Plateforme</h2>
            <p className="text-sm text-gray-500">Ce logo sera affiche dans la barre laterale</p>
          </div>
        </div>

        <div className="p-6">
          <div className="flex items-start gap-6">
            {/* Current Logo Preview */}
            <div className="flex-shrink-0">
              <div className="w-32 h-32 border-2 border-dashed border-gray-300 rounded-xl flex items-center justify-center bg-gray-50 overflow-hidden">
                {currentLogo ? (
                  <img
                    src={`${API_BASE}${currentLogo}`}
                    alt="Logo actuel"
                    className="max-w-full max-h-full object-contain p-2"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : (
                  <div className="text-center">
                    <div className="h-12 w-12 bg-primary-600 rounded-lg flex items-center justify-center mx-auto mb-2">
                      <span className="text-2xl font-bold text-white">N</span>
                    </div>
                    <span className="text-xs text-gray-400">Logo par defaut</span>
                  </div>
                )}
              </div>
            </div>

            {/* Upload Controls */}
            <div className="flex-1 space-y-4">
              <div>
                <h3 className="text-sm font-medium text-gray-700 mb-2">
                  Telecharger un nouveau logo
                </h3>
                <p className="text-xs text-gray-500 mb-3">
                  Formats acceptes: PNG, JPG, WebP, SVG. Taille maximale: 2 Mo.
                  Pour un meilleur rendu, utilisez une image de 200x200 pixels minimum.
                </p>

                <input
                  ref={fileInputRef}
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp,image/svg+xml"
                  onChange={handleLogoUpload}
                  className="hidden"
                  id="logo-upload"
                />

                <div className="flex items-center gap-3">
                  <label
                    htmlFor="logo-upload"
                    className={`
                      inline-flex items-center gap-2 px-4 py-2.5 rounded-lg font-medium text-sm
                      cursor-pointer transition-colors
                      ${uploadingLogo
                        ? 'bg-gray-100 text-gray-400 cursor-not-allowed'
                        : 'bg-indigo-600 text-white hover:bg-indigo-700'
                      }
                    `}
                  >
                    {uploadingLogo ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Telechargement...
                      </>
                    ) : (
                      <>
                        <Upload className="w-4 h-4" />
                        Choisir un fichier
                      </>
                    )}
                  </label>

                  {currentLogo && (
                    <button
                      type="button"
                      onClick={handleDeleteLogo}
                      disabled={deletingLogo}
                      className="inline-flex items-center gap-2 px-4 py-2.5 bg-red-50 text-red-600 rounded-lg font-medium text-sm hover:bg-red-100 transition-colors disabled:opacity-50"
                    >
                      {deletingLogo ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          Suppression...
                        </>
                      ) : (
                        <>
                          <Trash2 className="w-4 h-4" />
                          Supprimer le logo
                        </>
                      )}
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Settings Form */}
      <div className="bg-white rounded-xl border border-gray-200 shadow-sm">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3">
          <div className="p-2 bg-primary-100 rounded-lg">
            <Settings className="w-5 h-5 text-primary-600" />
          </div>
          <h2 className="font-semibold text-gray-900">Coordonnees de Contact</h2>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* Company Name */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom de la societe
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Building2 className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={formData.companyName}
                onChange={(e) => setFormData({ ...formData, companyName: e.target.value })}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="NEXEDU"
              />
            </div>
          </div>

          {/* Contact Phone */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Numero de telephone
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Phone className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={formData.contactPhone}
                onChange={(e) => setFormData({ ...formData, contactPhone: e.target.value })}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="+212 5 22 00 00 00"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Ce numero sera affiche aux DG et Responsables Financiers lorsque leur etablissement est suspendu
            </p>
          </div>

          {/* Contact Email */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Email de contact
            </label>
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <Mail className="w-5 h-5 text-gray-400" />
              </div>
              <input
                type="email"
                value={formData.contactEmail}
                onChange={(e) => setFormData({ ...formData, contactEmail: e.target.value })}
                className="w-full pl-10 pr-4 py-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                placeholder="facturation@nexedu.ma"
              />
            </div>
            <p className="mt-1 text-xs text-gray-500">
              Cet email sera affiche aux DG et Responsables Financiers lorsque leur etablissement est suspendu
            </p>
          </div>

          {/* Preview */}
          <div className="border-t border-gray-200 pt-6">
            <h3 className="text-sm font-medium text-gray-700 mb-3">Apercu sur la page de suspension</h3>
            <div className="bg-gray-50 rounded-lg p-4 border border-gray-200">
              <div className="text-sm font-medium text-gray-700 mb-2">Nous contacter</div>
              <div className="space-y-1">
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Phone className="w-4 h-4" />
                  <span>{formData.contactPhone || '+212 5 22 00 00 00'}</span>
                </div>
                <div className="flex items-center gap-2 text-sm text-gray-600">
                  <Mail className="w-4 h-4" />
                  <span>{formData.contactEmail || 'facturation@nexedu.ma'}</span>
                </div>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="flex justify-end pt-4 border-t border-gray-200">
            <button
              type="submit"
              disabled={saving}
              className="flex items-center gap-2 px-6 py-2.5 bg-primary-600 text-white rounded-lg hover:bg-primary-700 disabled:opacity-50 transition-colors font-medium"
            >
              {saving ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4" />
                  Enregistrer
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PlatformSettingsPage;
